import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, takeUntil, tap } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { AliasResourceType, AliasService } from 'app/providers/bridge-api/alias.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { generalAsyncNameValidator } from 'app/utils/customValidators';

@Component({
  selector: 'app-create-alias-dialog',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './create-alias-dialog.component.html',
  styleUrls: ['./create-alias-dialog.component.scss'],
})
export class CreateAliasDialogComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() dashboardId: string;

  @Output() modalClosed = new EventEmitter();

  name: string;

  shareable: boolean;
  nameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };

  nameValidator$ = new Subject();
  unsub$ = new Subject<void>();

  public isLoading = {
    save: false,
    validation: false,
  };

  private aliasService = inject(AliasService);
  private groupService = inject(GroupService);
  private alertService = inject(AlertService);

  constructor() {
    this.nameValidator$
      .pipe(
        takeUntil(this.unsub$),
        tap(() => (this.isLoading.validation = true)),
        debounceTime(500)
      )
      .subscribe(() => {
        generalAsyncNameValidator(
          this.name,
          this.nameStatus,
          translate('dashboard-options-component.create-alias-dialog.alias-label'),
          -1,
          true,
          () => this.aliasService.getAliasByTypeAndName(AliasResourceType.DASHBOARD, this.name),
          undefined,
          [],
          {
            regexp: /^[a-zA-Z0-9_\\-]+$/gi,
            description: translate('dashboard-options-component.create-alias-dialog.description'),
          }
        )
          .then((res) => (this.nameStatus = { ...res }))
          .finally(() => {
            this.isLoading.validation = false;
          });
      });
  }

  canCreate() {
    return (
      !this.isLoading.save &&
      !this.isLoading.validation &&
      this.name &&
      this.nameStatus.state !== EnumErrorStates.ERROR
    );
  }

  cancel() {
    this.modalClosed.emit();
  }

  create() {
    this.isLoading.save = true;

    this.aliasService
      .createAlias(
        AliasResourceType.DASHBOARD,
        this.dashboardId,
        this.name,
        this.groupService.getSelectedGroupId(),
        this.shareable
      )
      .then((res) => {
        this.alertService.showToast(
          translate('dashboard-options-component.create-alias-dialog.alias-created', {
            name: this.name,
          })
        );

        this.modalClosed.emit(res);
      })
      .catch((err) => {
        this.alertService.showErrorToast(
          translate('dashboard-options-component.create-alias-dialog.alias-failed'),
          err
        );
        this.isLoading.save = false;
      });
  }
}
